/**
 * Native push (Capacitor) wiring — iOS / iPadOS / Android.
 *
 * The Capacitor plugins (`@capacitor/push-notifications`,
 * `@capacitor/local-notifications`) are listed only in
 * `apps/mobile/package.json`. We dynamically import them so the web bundle
 * never has to resolve them. On non-native runtimes this module is a no-op.
 */
import { supabase } from "@/integrations/supabase/client";

let initPromise: Promise<void> | null = null;

function isCapacitorNative(): boolean {
  if (typeof window === "undefined") return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return !!(window as any).Capacitor?.isNativePlatform?.();
}

function getPlatform(): "ios" | "android" | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const p = (window as any).Capacitor?.getPlatform?.();
  if (p === "ios") return "ios";
  if (p === "android") return "android";
  return null;
}

/**
 * Initialise native push.
 * Safe to call multiple times; does the work exactly once per app session.
 */
export async function initNativePush(navigateToLink?: (link: string) => void): Promise<void> {
  if (!isCapacitorNative()) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      // Built from a runtime expression so TypeScript / Vite never try to
      // resolve these specifiers in the web bundle. The packages exist only
      // inside the Capacitor mobile app.
      const pushSpec = ["@capacitor", "push-notifications"].join("/");
      const localSpec = ["@capacitor", "local-notifications"].join("/");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pushMod: any = await import(/* @vite-ignore */ pushSpec).catch(() => null);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const localMod: any = await import(/* @vite-ignore */ localSpec).catch(() => null);
      if (!pushMod) {
        console.warn("[native-push] @capacitor/push-notifications not available");
        return;
      }
      const { PushNotifications } = pushMod;
      const LocalNotifications = localMod?.LocalNotifications;

      const platform = getPlatform();
      if (!platform) return;

      // 1) Permission
      const permStatus = await PushNotifications.checkPermissions();
      let receive = permStatus.receive;
      if (receive === "prompt" || receive === "prompt-with-rationale") {
        const req = await PushNotifications.requestPermissions();
        receive = req.receive;
      }
      if (receive !== "granted") {
        console.info("[native-push] permission not granted:", receive);
        return;
      }

      // 2) Register with APNs / FCM
      await PushNotifications.register();

      PushNotifications.addListener("registration", async (tok: { value: string }) => {
        try {
          await supabase.functions.invoke("register-device-token", {
            body: { platform, token: tok.value },
          });
        } catch (e) {
          console.error("[native-push] register-device-token failed:", e);
        }
      });

      PushNotifications.addListener("registrationError", (err: unknown) => {
        console.error("[native-push] registration error:", err);
      });

      // 3) Foreground delivery — iOS/Android both deliver silently when the
      //    app is in the foreground unless we surface it. Use local
      //    notifications so the user still sees a banner.
      PushNotifications.addListener(
        "pushNotificationReceived",
        async (n: { title?: string; body?: string; data?: Record<string, string> }) => {
          if (!LocalNotifications) return;
          try {
            await LocalNotifications.schedule({
              notifications: [
                {
                  id: Date.now() % 2147483647,
                  title: n.title ?? "Aicavite",
                  body: n.body ?? "",
                  extra: n.data ?? {},
                },
              ],
            });
          } catch (e) {
            console.warn("[native-push] local schedule failed:", e);
          }
        }
      );

      // 4) Tap → deep link
      PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (action: { notification?: { data?: Record<string, string> } }) => {
          const link = action?.notification?.data?.link ?? "/dashboard";
          navigateToLink?.(link);
        }
      );

      if (LocalNotifications) {
        LocalNotifications.addListener(
          "localNotificationActionPerformed",
          (action: { notification?: { extra?: Record<string, string> } }) => {
            const link = action?.notification?.extra?.link ?? "/dashboard";
            navigateToLink?.(link);
          }
        );
      }
    } catch (e) {
      console.error("[native-push] init failed:", e);
    }
  })();

  return initPromise;
}
